import React, { useState, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { AlertTriangle, AlertCircle } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomDropdown, { CustomDropdownOption } from "../../common/general/CustomDropDown/CustomDropdown";
import AxioCard from "../../axio-ds/Card/AxioCard";
import { IconCircle, getIconCircleColor, IconCircleVariant } from "./IconCircle";
import { ModerationFlag, AssignedUser, availableUsers } from "./types";
import { colors, radii, spacing } from "../../../styles/design-system";

export interface ModerationFlagCardProps {
  flag: ModerationFlag;
  assignedUser: AssignedUser | null;
  onToggle: () => void;
  onAssignUser: (userId: string | null) => void;
}

function ModerationFlagCard({ flag, assignedUser, onToggle, onAssignUser }: ModerationFlagCardProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownOptions: CustomDropdownOption[] = useMemo(
    () =>
      availableUsers.map((user) => ({
        label: `${user.name} - ${user.role}`,
        value: user.id,
      })),
    []
  );

  const getFlagVariant = (iconType: ModerationFlag["icon"]): IconCircleVariant => {
    switch (iconType) {
      case "sexual":
      case "selfHarm":
        return "purple-muted";
      case "hate":
      case "violence":
        return "error-muted";
      case "harassment":
        return "orange-muted";
    }
  };

  const getFlagIconElement = (iconType: ModerationFlag["icon"]) => {
    const iconSize = 20;
    const variant = getFlagVariant(iconType);
    const iconColor = getIconCircleColor(variant);

    switch (iconType) {
      case "sexual":
      case "hate":
      case "harassment":
      case "violence":
        return <AlertTriangle size={iconSize} color={iconColor} />;
      case "selfHarm":
        return <AlertCircle size={iconSize} color={iconColor} />;
    }
  };

  return (
    <AxioCard
      variant="basic"
      disabled={!flag.enabled}
      style={[styles.card, !flag.enabled && styles.cardDisabled, dropdownOpen && styles.cardDropdownOpen]}>
      <View style={styles.cardContent}>
        {/* Icon */}
        <IconCircle variant={getFlagVariant(flag.icon)} rounded="lg" icon={getFlagIconElement(flag.icon)} />

        {/* Content */}
        <View style={styles.textContainer}>
          <View style={styles.titleRow}>
            <CustomText text={flag.name} size="xl" weight="bold" useTranslationText={false} style={styles.title} />
            <View style={styles.idBadge}>
              <CustomText text={flag.id} size="xs" useTranslationText={false} style={styles.idText} />
            </View>
          </View>
          <CustomSpacing type="vertical" size="xs" />
          <CustomText text={flag.description} size="s" useTranslationText={false} style={styles.description} />
          <CustomSpacing type="vertical" size="s" />

          {/* Do not remove this flag. Will be implemented in the next phase when we connect to backend. */}
          {/* User Assignment - only show when enabled */}
          {/* {flag.enabled && (
            <>
              <CustomSpacing type="vertical" size="s" />
              <View style={[styles.assignmentRow, dropdownOpen && styles.assignmentRowOpen]}>
                <View style={styles.assignmentContainer}>
                  <CustomText text="Assigned to:" size="s" useTranslationText={false} style={styles.assignmentLabel} />
                  <View style={styles.dropdownContainer}>
                    <CustomDropdown
                      options={dropdownOptions}
                      value={flag.assignedUserId}
                      onChange={(value) => onAssignUser(value || null)}
                      placeholder="Select a reviewer..."
                      fullWidth={false}
                      accessibilityLabel={`Select reviewer for ${flag.name}`}
                      open={dropdownOpen}
                      onOpenChange={setDropdownOpen}
                      dropdownStyle={{
                        backgroundColor: colors.white,
                        borderColor: colors.grey[200],
                        borderRadius: radii.md,
                        minHeight: 40,
                      }}
                      dropdownContainerStyle={{
                        backgroundColor: colors.white,
                        borderColor: colors.grey[200],
                        borderRadius: radii.md,
                        minWidth: 350,
                      }}
                    />
                  </View>
                </View>
                {assignedUser ? (
                  <CustomText
                    text={assignedUser.email}
                    size="xs"
                    useTranslationText={false}
                    style={styles.assignedEmail}
                  />
                ) : (
                  <CustomText
                    text="No reviewer assigned"
                    size="xs"
                    useTranslationText={false}
                    style={styles.noReviewerText}
                  />
                )}
              </View>
            </>
          )} */}
        </View>

        {/* Toggle */}
        <CustomSwitch
          value={flag.enabled}
          onValueChange={onToggle}
          accessibilityLabel={`Toggle ${flag.name}`}
          size="m"
        />
      </View>
    </AxioCard>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radii.xl,
    zIndex: 1,
    overflow: "visible",
  },
  cardDisabled: {
    opacity: 0.75,
  },
  cardDropdownOpen: {
    zIndex: 5000,
  },
  cardContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
    overflow: "visible",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  title: {
    color: colors.grey[900],
  },
  idBadge: {
    backgroundColor: colors.grey[100],
    borderRadius: radii.xs,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  idText: {
    color: colors.grey[500],
    fontFamily: "monospace",
  },
  description: {
    color: colors.grey[600],
  },
  assignmentRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    zIndex: 1,
  },
  assignmentRowOpen: {
    zIndex: 5000,
  },
  assignmentContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  assignmentLabel: {
    color: colors.grey[600],
    flexShrink: 0,
  },
  dropdownContainer: {
    zIndex: 5000,
  },
  assignedEmail: {
    color: colors.grey[500],
  },
  noReviewerText: {
    color: colors.warning[700],
  },
});

export default ModerationFlagCard;
